import { useState } from "react";
import { useEscapeKey } from '../hooks/useKeyboardShortcuts';
import { createPortal } from "react-dom";
import { aufTaste } from '../utils/tastaturBedienung';
// Die API-Basis kommt aus `config.js` und wird hier **nicht** noch einmal
// aus der Umgebung gelesen (L-145). Bis zum 28.08.2026 stand hier
// `process.env.REACT_APP_API_URL || ""` — und `""` heisst relative Adresse,
// also die eigene Herkunft. Fehlt die Variable, haetten die fuenf Aufrufe
// dieser Komponente an die Static Site gefragt und HTML statt JSON bekommen:
// genau der Produktivausfall vom selben Tag, nur dauerhaft und still.
import API_BASE_URL from '../config';

export default function NewProjectModal({ onClose, onProjectCreated }) {
  // **Escape schliesst — WCAG 2.1.1 (30.08.2026, L-17).** Der Hintergrund
  // dieses Modals reagiert auf einen Klick; mit der Tastatur gab es keinen
  // Weg heraus ausser dem Suchen des Abbrechen-Knopfes. `role="button"` waere
  // hier falsch: Eine Ueberlagerung ist keine Schaltflaeche.
  useEscapeKey(onClose);

  const token = localStorage.getItem("kompagnon_token");
  const h = { "Content-Type": "application/json", Authorization: `Bearer ${token}` };

  // Stufe: "search" | "create"
  const [stufe, setStufe] = useState("search");
  const [suche, setSuche] = useState("");
  const [sucheErgebnisse, setSucheErgebnisse] = useState([]);
  const [sucheLoading, setSucheLoading] = useState(false);

  const [form, setForm] = useState({
    company_name: "",
    website_url: "",
    contact_name: "",
    email: "",
    trade: "",
  });
  const [saving, setSaving] = useState(false);
  const [fehler, setFehler] = useState("");

  const handleSuche = async (val) => {
    setSuche(val);
    if (val.length < 2) { setSucheErgebnisse([]); return; }
    setSucheLoading(true);
    try {
      const res = await fetch(`${API_BASE_URL}/api/leads/?search=${encodeURIComponent(val)}&limit=8`, { headers: h });
      const data = await res.json();
      const leads = Array.isArray(data) ? data : (data.items || []);
      setSucheErgebnisse(leads);
    } catch { setSucheErgebnisse([]); }
    finally { setSucheLoading(false); }
  };

  const handleLeadWaehlen = async (lead) => {
    if (!lead.website_url) {
      setFehler(`"${lead.company_name}" hat keine Domain hinterlegt. Bitte erst die Domain ergänzen oder ein neues Unternehmen anlegen.`);
      setStufe("create");
      setForm(f => ({ ...f, company_name: lead.company_name }));
      return;
    }
    setSaving(true);
    setFehler("");
    try {
      const res = await fetch(`${API_BASE_URL}/api/projects/from-lead/${lead.id}`, { method: "POST", headers: h });
      const data = await res.json();
      if (res.ok) {
        onProjectCreated(data);
        onClose();
      } else {
        setFehler(data?.detail?.message || data?.detail || "Fehler beim Anlegen des Projekts.");
      }
    } catch { setFehler("Verbindungsfehler. Bitte erneut versuchen."); }
    finally { setSaving(false); }
  };

  const handleNeuAnlegen = async () => {
    setFehler("");
    if (!form.company_name.trim()) { setFehler("Bitte Firmenname eingeben."); return; }
    if (!form.website_url.trim()) { setFehler("Bitte Website-Domain eingeben."); return; }
    setSaving(true);
    try {
      const leadRes = await fetch(`${API_BASE_URL}/api/leads/`, {
        method: "POST", headers: h, body: JSON.stringify({ ...form, status: "new", legacy_type: "lead" }),
      });
      const lead = await leadRes.json();
      if (!leadRes.ok) { setFehler(lead?.detail || "Fehler beim Anlegen des Unternehmens."); return; }

      const projRes = await fetch(`${API_BASE_URL}/api/projects/from-lead/${lead.id}`, { method: "POST", headers: h });
      const proj = await projRes.json();
      if (projRes.ok) {
        onProjectCreated(proj);
        onClose();
      } else {
        setFehler(proj?.detail?.message || proj?.detail || "Projekt konnte nicht gestartet werden.");
      }
    } catch { setFehler("Verbindungsfehler. Bitte erneut versuchen."); }
    finally { setSaving(false); }
  };

  return createPortal(
    <div
      onClick={onClose}
      style={{
        position: "fixed", inset: 0, background: "rgba(0,0,0,0.55)",
        display: "flex", alignItems: "center", justifyContent: "center", zIndex: 1000,
        padding: 16,
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          background: "#fff", borderRadius: 12, padding: 32, width: "100%", maxWidth: 520,
          boxShadow: "0 8px 40px rgba(0,0,0,0.18)",
          maxHeight: "calc(100vh - 32px)", overflowY: "auto",
        }}
      >
        <h2 style={{ margin: "0 0 4px", fontSize: 20, color: "var(--brand-primary)" }}>Neues Projekt starten</h2>
        <p style={{ margin: "0 0 24px", color: "#666", fontSize: 14 }}>
          Bitte wähle ein bestehendes Unternehmen oder lege ein neues an.
        </p>

        {/* Tab-Wahl */}
        <div style={{ display: "flex", gap: 8, marginBottom: 20 }}>
          <button
            onClick={() => setStufe("search")}
            style={{
              flex: 1, padding: "8px 0", borderRadius: 8, border: "none", cursor: "pointer",
              background: stufe === "search" ? "var(--kc-mid)" : "#f0f0f0",
              color: stufe === "search" ? "#fff" : "#333", fontWeight: 600, fontSize: 14,
            }}
          >Bestehendes Unternehmen</button>
          <button
            onClick={() => setStufe("create")}
            style={{
              flex: 1, padding: "8px 0", borderRadius: 8, border: "none", cursor: "pointer",
              background: stufe === "create" ? "var(--kc-mid)" : "#f0f0f0",
              color: stufe === "create" ? "#fff" : "#333", fontWeight: 600, fontSize: 14,
            }}
          >Neues Unternehmen</button>
        </div>

        {/* Stufe: Suche */}
        {stufe === "search" && (
          <div>
            <input aria-label="Firmenname suchen..."
              type="text"
              placeholder="Firmenname suchen..."
              value={suche}
              onChange={e => handleSuche(e.target.value)}
              style={{ width: "100%", padding: "10px 14px", borderRadius: 8, border: "1px solid #ddd", fontSize: 14, boxSizing: "border-box" }}
            />
            {sucheLoading && <p style={{ color: "#888", fontSize: 13, margin: "8px 0 0" }}>Suche...</p>}
            {sucheErgebnisse.length > 0 && (
              <div style={{ marginTop: 8, border: "1px solid #eee", borderRadius: 8, overflow: "hidden" }}>
                {sucheErgebnisse.map(lead => (
                  <div role="button" tabIndex={0} onKeyDown={aufTaste(() => handleLeadWaehlen(lead))}
                    key={lead.id}
                    onClick={() => handleLeadWaehlen(lead)}
                    style={{
                      padding: "10px 14px", cursor: "pointer", borderBottom: "1px solid #f0f0f0",
                      display: "flex", justifyContent: "space-between", alignItems: "center",
                    }}
                    onMouseEnter={e => e.currentTarget.style.background = "#f5f5f5"}
                    onMouseLeave={e => e.currentTarget.style.background = "#fff"}
                  >
                    <div>
                      <div style={{ fontWeight: 600, fontSize: 14 }}>{lead.company_name}</div>
                      <div style={{ fontSize: 12, color: lead.website_url ? "var(--kc-mid)" : "var(--warn)" }}>
                        {lead.website_url || "⚠ Keine Domain hinterlegt"}
                      </div>
                    </div>
                    <span style={{ fontSize: 12, color: "#999" }}>→</span>
                  </div>
                ))}
              </div>
            )}
            {suche.length >= 2 && !sucheLoading && sucheErgebnisse.length === 0 && (
              <p style={{ color: "#888", fontSize: 13, margin: "8px 0 0" }}>
                Kein Unternehmen gefunden.{" "}
                <button onClick={() => setStufe("create")} style={{ color: "var(--text-brand)", background: "none", border: "none", cursor: "pointer", padding: 0, textDecoration: "underline" }}>Neu anlegen →</button>
              </p>
            )}
          </div>
        )}

        {/* Stufe: Neu anlegen */}
        {stufe === "create" && (
          <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
            {[
              { key: "company_name", label: "Firmenname *", placeholder: "z.B. Müller Haustechnik GmbH", type: "text" },
              { key: "website_url", label: "Website / Domain *", placeholder: "z.B. https://muellerhaustechnik.de", type: "text" },
              { key: "contact_name", label: "Ansprechpartner", placeholder: "z.B. Hans Müller", type: "text" },
              { key: "email", label: "E-Mail", placeholder: "z.B. info@muellerhaustechnik.de", type: "email" },
              { key: "trade", label: "Gewerk / Branche", placeholder: "z.B. Sanitär, Heizung, Klima", type: "text" },
            ].map(({ key, label, placeholder, type }) => (
              <div key={key}>
                <label style={{ display: "block", fontSize: 13, fontWeight: 600, color: "#444", marginBottom: 4 }}>{label}</label>
                <input aria-label={placeholder}
                  type={type}
                  placeholder={placeholder}
                  value={form[key]}
                  onChange={e => setForm(f => ({ ...f, [key]: e.target.value }))}
                  style={{ width: "100%", padding: "9px 14px", borderRadius: 8, border: "1px solid #ddd", fontSize: 14, boxSizing: "border-box" }}
                />
              </div>
            ))}
          </div>
        )}

        {/* Fehlermeldung */}
        {fehler && (
          <div style={{ marginTop: 16, padding: "10px 14px", background: "#fef2f2", border: "1px solid #fecaca", borderRadius: 8, color: "#dc2626", fontSize: 13 }}>
            {fehler}
          </div>
        )}

        {/* Buttons */}
        <div style={{ display: "flex", justifyContent: "flex-end", gap: 10, marginTop: 24 }}>
          <button
            onClick={onClose}
            disabled={saving}
            style={{ padding: "9px 20px", borderRadius: 8, border: "1px solid #ddd", background: "#fff", cursor: "pointer", fontSize: 14 }}
          >Abbrechen</button>
          {stufe === "create" && (
            <button
              onClick={handleNeuAnlegen}
              disabled={saving}
              style={{
                padding: "9px 20px", borderRadius: 8, border: "none", cursor: "pointer",
                background: "var(--kc-yellow)", color: "#000", fontWeight: 700, fontSize: 14,
              }}
            >{saving ? "Wird angelegt..." : "Unternehmen anlegen & Projekt starten"}</button>
          )}
        </div>
      </div>
    </div>,
    document.body,
  );
}
